import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CreditCard, Smartphone, CheckCircle2, XCircle, Clock, ShieldCheck } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { useI18n } from "@/i18n";
import { ThemeToggle } from "@/context/theme";
import { Btn, Input, fmtMoney } from "@/components/ui-rp";

const pub = axios.create({ baseURL: API });

const Frame = ({ tenant, children }) => {
  const { lang, setLang } = useI18n();
  const color = tenant?.branding?.color;
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center px-4 py-6 sm:py-12" style={color ? { "--primary-override": color } : undefined}>
      <div className="w-full max-w-md flex items-center justify-between mb-6">
        {tenant?.branding?.logo_url ? <img src={tenant.branding.logo_url} alt={tenant.name} className="h-9 max-w-[180px] object-contain" /> : <span className="font-heading font-bold text-lg" data-testid="checkout-merchant-name">{tenant?.branding?.display_name || tenant?.name || "RapidPay"}</span>}
        <div className="flex items-center gap-1"><ThemeToggle /><button data-testid="checkout-language-toggle" onClick={() => setLang(lang === "es" ? "en" : "es")} className="text-xs font-semibold px-3 min-h-[44px] rounded-full hover:bg-secondary">{lang === "es" ? "EN" : "ES"}</button></div>
      </div>
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-lg p-6 sm:p-8" data-testid="checkout-card">{children}</div>
      <div className="w-full max-w-md mt-6 flex flex-col items-center gap-3 text-center">
        <div className="flex items-center gap-3 opacity-80">
          <img src="/logos/logo_visa.png" alt="Visa" className="h-5 object-contain" /><img src="/logos/logo_mastercard.png" alt="Mastercard" className="h-5 object-contain" /><img src="/logos/logo_yappy.png" alt="Yappy" className="h-5 object-contain" /><img src="/logos/sec_pci.png" alt="PCI" className="h-5 object-contain" />
        </div>
        <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" />RapidPay</p>
      </div>
    </div>
  );
};

const Result = ({ order, t, tenant }) => {
  const map = { paid: [CheckCircle2, "text-[hsl(122,87%,30%)] dark:text-primary", "paidTitle", "paidBody"], rejected: [XCircle, "text-destructive", "rejectedTitle", "rejectedBody"], expired: [Clock, "text-muted-foreground", "expiredTitle", "expiredBody"], voided: [XCircle, "text-muted-foreground", "expiredTitle", "expiredBody"], refunded: [CheckCircle2, "text-muted-foreground", "paidTitle", "paidBody"] };
  const [Icon, cls, title, body] = map[order.status] || map.expired;
  const back = order.status === "paid" ? order.success_url : order.cancel_url;
  useEffect(() => { if (window.parent !== window) window.parent.postMessage({ rapidpay: true, status: order.status, order: order.order_number }, "*"); }, [order]);
  return (
    <div className="flex flex-col items-center text-center gap-3" data-testid={`checkout-result-${order.status}`}>
      <Icon className={`w-16 h-16 ${cls}`} />
      <h1 className="font-heading text-2xl font-bold">{t(`checkout.${title}`)}</h1>
      <p className="text-sm text-muted-foreground">{t(`checkout.${body}`)}</p>
      <p className="font-heading text-3xl font-extrabold mt-2">{fmtMoney(order.amount)}</p>
      <p className="text-xs text-muted-foreground font-mono">{t("checkout.ref")}: {order.order_number}</p>
      {back && <a href={back} className="mt-3 text-sm font-semibold underline" data-testid="checkout-back-link">{t("checkout.back")}</a>}
    </div>
  );
};

export default function Checkout() {
  const { slug, linkId, orderId } = useParams();
  const [params] = useSearchParams();
  const { t, lang } = useI18n();
  const [tenant, setTenant] = useState(null);
  const [link, setLink] = useState(null);
  const [order, setOrder] = useState(null);
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [pay, setPay] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState("");

  const loadOrder = useCallback(async (id) => {
    const { data } = await pub.get(`/public/orders/${id}`);
    setTenant(data.tenant);
    setOrder(data.order);
    return data.order;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (orderId) await loadOrder(orderId);
        else {
          const { data } = await pub.get(`/public/checkout/${slug}/${linkId}`, { headers: { "Accept-Language": lang } });
          setTenant(data.tenant);
          setLink(data.link);
        }
      } catch (e) {
        setError(e.response?.data?.detail?.message || t("checkout.notFound"));
      }
    })();
  }, [slug, linkId, orderId, loadOrder, lang, t]);

  useEffect(() => {
    if (!order || !["created", "pending"].includes(order.status)) return undefined;
    const tick = () => {
      const ms = new Date(order.expires_at) - Date.now();
      if (ms <= 0) { loadOrder(order.id); return; }
      setLeft(`${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`);
    };
    tick();
    const i = setInterval(tick, 1000);
    const poll = setInterval(() => order.status === "pending" && pay?.provider !== "mock" && loadOrder(order.id), 4000);
    return () => { clearInterval(i); clearInterval(poll); };
  }, [order, loadOrder, pay]);

  const createOrder = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await pub.post(`/public/checkout/${slug}/${linkId}/orders`, { amount: link.amount ?? Number(amount), email }, { headers: { "Accept-Language": lang } });
      setOrder(data.order);
      window.history.replaceState(null, "", `/pagar/${slug}/o/${data.order.id}${params.get("embed") ? "?embed=1" : ""}`);
    } catch (err) {
      setError(err.response?.data?.detail?.message || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const startPay = async (method) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await pub.post(`/public/orders/${order.id}/pay`, { method, email: email || undefined }, { headers: { "Accept-Language": lang } });
      setOrder(data.order);
      setPay(data);
      if (data.provider === "cybersource") {
        const f = document.createElement("form");
        f.method = "POST"; f.action = data.action;
        Object.entries(data.fields).forEach(([k, v]) => { const i = document.createElement("input"); i.type = "hidden"; i.name = k; i.value = v ?? ""; f.appendChild(i); });
        document.body.appendChild(f); f.submit();
      }
      if (data.provider === "yappy" && !document.querySelector(`script[src="${data.cdn}"]`)) {
        const s = document.createElement("script"); s.src = data.cdn; document.head.appendChild(s);
      }
    } catch (err) {
      setError(err.response?.data?.detail?.message || t("common.error"));
      if (err.response?.status === 409) loadOrder(order.id);
    } finally {
      setBusy(false);
    }
  };

  const mock = async (outcome) => {
    setBusy(true);
    try {
      const { data } = await pub.post(`/public/orders/${order.id}/mock/complete`, { outcome });
      setOrder(data);
      setPay(null);
    } finally {
      setBusy(false);
    }
  };

  if (error && !order && !link) return <Frame tenant={tenant}><p className="text-center text-sm text-muted-foreground" data-testid="checkout-error">{error}</p></Frame>;
  if (!tenant) return <Frame><div className="h-40 animate-pulse rounded-xl bg-secondary" /></Frame>;
  if (order && !["created", "pending"].includes(order.status)) return <Frame tenant={tenant}><Result order={order} t={t} tenant={tenant} /></Frame>;

  const methods = [["card", CreditCard, t("checkout.card")], ["yappy", Smartphone, t("checkout.yappy")]].filter(([m]) => tenant.payment_methods?.[m]);

  return (
    <Frame tenant={tenant}>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("checkout.payTo")}</p>
      <p className="font-heading font-bold text-lg">{tenant.branding?.display_name || tenant.name}</p>
      <p className="text-sm text-muted-foreground mb-5">{order?.description || link?.title}{link?.description ? ` · ${link.description}` : ""}</p>

      {!order ? (
        <form onSubmit={createOrder} className="space-y-4">
          {link.amount != null ? <p className="font-heading text-4xl font-extrabold" data-testid="checkout-amount">{fmtMoney(link.amount)}</p> : (
            <label className="block"><span className="block text-sm font-medium mb-1.5">{t("checkout.enterAmount")}</span>
              <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-muted-foreground">$</span><Input testId="checkout-amount-input" type="number" step="0.01" min="0.01" inputMode="decimal" required value={amount} onChange={(e) => setAmount(e.target.value)} className="h-16 pl-10 text-3xl font-heading font-extrabold" /></div></label>
          )}
          <label className="block"><span className="block text-sm font-medium mb-1.5">{t("checkout.email")}</span><Input testId="checkout-email-input" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Btn type="submit" busy={busy} testId="checkout-continue-button" className="w-full h-12 text-base">{t("checkout.pay")}</Btn>
        </form>
      ) : pay?.provider === "mock" ? (
        <div className="space-y-4" data-testid="checkout-mock-panel">
          <p className="font-heading text-4xl font-extrabold">{fmtMoney(order.amount)}</p>
          <div className="rounded-xl border border-dashed border-amber-500/60 bg-amber-500/10 p-4"><p className="text-sm font-bold text-amber-700 dark:text-amber-400">{t("checkout.simTitle")}</p><p className="text-xs text-muted-foreground mt-1">{t("checkout.simHelp")} ({t(`method.${pay.method}`)})</p></div>
          <Btn busy={busy} onClick={() => mock("approved")} testId="mock-approve-button" className="w-full h-12">{t("checkout.approve")}</Btn>
          <Btn variant="danger" busy={busy} onClick={() => mock("declined")} testId="mock-decline-button" className="w-full h-12">{t("checkout.decline")}</Btn>
        </div>
      ) : pay?.provider === "cybersource" ? (
        <p className="text-sm text-muted-foreground animate-pulse" data-testid="checkout-redirecting">{t("checkout.redirecting")}</p>
      ) : pay?.provider === "yappy" ? (
        <div className="space-y-3" data-testid="checkout-yappy-panel">
          <p className="font-heading text-4xl font-extrabold">{fmtMoney(order.amount)}</p>
          <p className="text-sm text-muted-foreground">{t("checkout.yappyHelp")}</p>
          <btn-yappy theme="blue" rounded="true" ref={(el) => { if (el && pay.payment && !el._rp) { el._rp = true; el.addEventListener("eventClick", () => el.eventPayment?.(pay.payment)); setTimeout(() => el.eventPayment?.(pay.payment), 300); } }} />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between"><p className="font-heading text-4xl font-extrabold" data-testid="checkout-amount">{fmtMoney(order.amount)}</p>{left && <span className="text-xs text-muted-foreground font-mono" data-testid="checkout-timer">{t("checkout.expiresIn")} {left}</span>}</div>
          <p className="text-sm font-medium">{t("checkout.choose")}</p>
          {methods.map(([m, Icon, label]) => (
            <button key={m} disabled={busy} onClick={() => startPay(m)} data-testid={`checkout-method-${m}`} className="w-full flex items-center gap-3 rounded-xl border border-border px-4 min-h-[56px] text-left hover:border-primary hover:bg-primary/5 active:scale-[0.99] transition-[border-color,background-color,transform] disabled:opacity-50">
              <Icon className="w-5 h-5 text-[hsl(122,87%,30%)] dark:text-primary" /><span className="font-semibold text-sm flex-1">{label}</span>
              {m === "card" ? <span className="flex gap-1"><img src="/logos/logo_visa.png" alt="" className="h-4 object-contain" /><img src="/logos/logo_mastercard.png" alt="" className="h-4 object-contain" /></span> : <img src="/logos/logo_yappy.png" alt="" className="h-4 object-contain" />}
            </button>
          ))}
          {error && <p className="text-sm text-destructive" data-testid="checkout-pay-error">{error}</p>}
          <p className="text-[11px] text-muted-foreground pt-2">{t("checkout.secure")}</p>
        </div>
      )}
    </Frame>
  );
}
